import { parse } from '@/minidown/parser'
import { type MinidownNode, MinidownNodeType } from '@/minidown/nodes'
import {
  type MouseEvent,
  type ReactNode,
  useMemo,
  createElement,
  Fragment,
} from 'react'

interface Props {
  /**
   * The Minidown content to render. Can be either the raw source or the
   * parsed AST.
   */
  value: string | MinidownNode
  /**
   * See {@link MinidownClassNames}.
   */
  classNames?: MinidownClassNames
  /**
   * Callback invoked when a link is pressed. If provided, prevents the default
   * navigation behavior.
   */
  onLinkPress?: (url: string) => void
}

/**
 * Class names to apply to rendered tags.
 */
export interface MinidownClassNames {
  p?: string
  a?: string
  strong?: string
  em?: string
}

/**
 * Renders Minidown as React components.
 */
export function MinidownContent({
  value,
  classNames,
  onLinkPress: onLinkPress,
}: Props) {
  const visit = useMemo(
    () => createVisitor(classNames, onLinkPress),
    [classNames, onLinkPress],
  )

  const root = useMemo<MinidownNode>(() => {
    if (typeof value !== 'string') {
      return value
    }

    try {
      return parse(value)
    } catch (err) {
      console.error(
        `Unable to parse Minidown. The Minidown source is:\n\n${value}`,
        err,
      )
      // Return the Minidown source as-is if we can't render it.
      return {
        type: MinidownNodeType.Root,
        children: [{ type: MinidownNodeType.Text, value }],
      }
    }
  }, [value])
  return createElement(Fragment, {}, ...visit(root))
}

/**
 * Creates a visitor that renders a React node for each AST node.
 */
function createVisitor(
  classNames: MinidownClassNames | undefined,
  onLinkPress: ((url: string) => void) | undefined,
) {
  return function visit(node: MinidownNode): ReactNode[] {
    switch (node.type) {
      case MinidownNodeType.Root:
        return node.children.flatMap(visit)
      case MinidownNodeType.Text:
        return [node.value]
      case MinidownNodeType.Break:
        return [createElement('br')]
      case MinidownNodeType.Emphasis:
        return [
          createElement(
            'em',
            {
              className: classNames?.em,
            },
            ...node.children.flatMap(visit),
          ),
        ]
      case MinidownNodeType.Strong:
        return [
          createElement(
            'strong',
            {
              className: classNames?.strong,
            },
            ...node.children.flatMap(visit),
          ),
        ]
      case MinidownNodeType.Paragraph:
        return [
          createElement(
            'p',
            {
              className: classNames?.p,
            },
            ...node.children.flatMap(visit),
          ),
        ]
      case MinidownNodeType.Link:
        return [
          createElement(
            'a',
            {
              className: classNames?.a,
              href: node.url,
              target: '_blank',
              onClick: onLinkPress
                ? (e: MouseEvent) => {
                    e.preventDefault()
                    onLinkPress(node.url)
                  }
                : undefined,
            },
            ...node.children.flatMap(visit),
          ),
        ]
      default:
        return []
    }
  }
}
